import { dashboardService } from '@/services/dashboard.service';
import type {
  AudioEliminadoReasignacionDTO,
  AudioSysadminDTO,
  CambiarCombinacionNtcResponse,
  CompartirAudioResponse,
  CopyDriveFileRequest,
  CopyDriveFileResponse,
  CreateEmpresaData,
  CreateEmpresaResponse,
} from '@/services/sysadmin.service';
import {
  sysadminService,
  type ActualizarAsesorRequest,
  type AsesorFormUrlsResponse,
  type AsesorSysadmin,
  type AsesoriaHorasPorRechazoDto,
  type AsesoriaSinEvaluacionAprobadaDto,
  type CombinacionNtcInfo,
  type CrearAsesorRequest,
  type CrearAsociacionesBatchRequest,
  type EliminarAsociacionesBatchRequest,
  type DriveFileItemDto,
  type DuplicateGroupDto,
  type EmpresaAnomaliaDto,
  type EmpresaDesincronizadaDto,
  type EmpresaHorasDesincronizadaDto,
  type EmpresaPorCombinacion,
  type EmpresaSysadmin,
  type GenericResponse,
  type ListarAsociacionesResponse,
  type ListarTernasResponse,
  type NtcDisponible,
  type RequisitoEmpresaEstado,
  type RequisitoSysadmin,
} from '@/services/sysadmin.service';
import type {
  ActualizarRequisitoRequest,
  ActualizarRequisitoResponse,
  CrearRequisitoRequest,
  CrearRequisitoResponse,
  DashboardEmpresa,
} from '@/types/types';
import { useCallback, useEffect, useState } from 'react';

// ========================================
// Hook: Asesores
// ========================================

interface UseAsesoresResult {
  asesores: AsesorSysadmin[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  crearAsesor: (data: CrearAsesorRequest) => Promise<void>;
  actualizarAsesor: (asesorId: number, data: ActualizarAsesorRequest) => Promise<void>;
  toggleEstado: (asesorId: number) => Promise<void>;
}

export function useAsesores(): UseAsesoresResult {
  const [asesores, setAsesores] = useState<AsesorSysadmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAsesores = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarAsesores();
      if (res.success) setAsesores(res.asesores);
      else setError('Error al cargar asesores');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesores');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAsesores();
  }, [fetchAsesores]);

  const crearAsesor = useCallback(
    async (data: CrearAsesorRequest) => {
      await sysadminService.crearAsesor(data);
      await fetchAsesores();
    },
    [fetchAsesores]
  );

  const actualizarAsesor = useCallback(
    async (asesorId: number, data: ActualizarAsesorRequest) => {
      await sysadminService.actualizarAsesor(asesorId, data);
      await fetchAsesores();
    },
    [fetchAsesores]
  );

  const toggleEstado = useCallback(
    async (asesorId: number) => {
      await sysadminService.toggleEstadoAsesor(asesorId);
      await fetchAsesores();
    },
    [fetchAsesores]
  );

  return { asesores, isLoading, error, refetch: fetchAsesores, crearAsesor, actualizarAsesor, toggleEstado };
}

// ========================================
// Hook: Asesor Form URLs
// ========================================

interface UseAsesorFormUrlsResult {
  isLoading: boolean;
  error: string | null;
  getFormUrls: (asesorId: number) => Promise<AsesorFormUrlsResponse>;
}

export function useAsesorFormUrls(): UseAsesorFormUrlsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getFormUrls = useCallback(async (asesorId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.getFormUrlsAsesor(asesorId);
      if (!res.success) setError('Error al obtener URLs del formulario');
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al obtener URLs del formulario';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, getFormUrls };
}

// ========================================
// Hook: Empresas (sysadmin - todas)
// ========================================

interface UseEmpresasSysadminResult {
  empresas: EmpresaSysadmin[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  asignarAsesor: (nit: string, asesorId: number | null) => Promise<void>;
  asignarCombinacion: (nit: string, combinacionId: number | null) => Promise<void>;
  crearEmpresa: (data: CreateEmpresaData) => Promise<CreateEmpresaResponse>;
  cambiarCombinacionNtc: (nit: string, combinacionNtcId: number | null) => Promise<CambiarCombinacionNtcResponse>;
  eliminarEmpresa: (nit: string) => Promise<void>;
  reactivarEmpresa: (nit: string) => Promise<void>;
}

export function useEmpresasSysadmin(): UseEmpresasSysadminResult {
  const [empresas, setEmpresas] = useState<EmpresaSysadmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEmpresas = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarTodasEmpresas();
      if (res.success) setEmpresas(res.empresas);
      else setError('Error al cargar empresas');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  const asignarAsesor = useCallback(
    async (nit: string, asesorId: number | null) => {
      await sysadminService.asignarAsesorEmpresa(nit, asesorId);
      await fetchEmpresas();
    },
    [fetchEmpresas]
  );

  const asignarCombinacion = useCallback(
    async (nit: string, combinacionId: number | null) => {
      await sysadminService.asignarCombinacion(nit, combinacionId);
      await fetchEmpresas();
    },
    [fetchEmpresas]
  );

  const crearEmpresa = useCallback(
    async (data: CreateEmpresaData): Promise<CreateEmpresaResponse> => {
      const res = await sysadminService.crearEmpresa(data);
      await fetchEmpresas();
      return res;
    },
    [fetchEmpresas]
  );

  const cambiarCombinacionNtc = useCallback(
    async (nit: string, combinacionNtcId: number | null): Promise<CambiarCombinacionNtcResponse> => {
      const res = await sysadminService.cambiarCombinacionNtc(nit, combinacionNtcId);
      await fetchEmpresas();
      return res;
    },
    [fetchEmpresas]
  );

  const eliminarEmpresa = useCallback(
    async (nit: string) => {
      await sysadminService.eliminarEmpresa(nit);
      await fetchEmpresas();
    },
    [fetchEmpresas]
  );

  const reactivarEmpresa = useCallback(
    async (nit: string) => {
      await sysadminService.reactivarEmpresa(nit);
      await fetchEmpresas();
    },
    [fetchEmpresas]
  );

  return {
    empresas,
    isLoading,
    error,
    refetch: fetchEmpresas,
    asignarAsesor,
    asignarCombinacion,
    crearEmpresa,
    cambiarCombinacionNtc,
    eliminarEmpresa,
    reactivarEmpresa,
  };
}

// ========================================
// Hook: Combinaciones NTC
// ========================================

interface UseCombinacionesResult {
  combinaciones: CombinacionNtcInfo[];
  isLoading: boolean;
  error: string | null;
}

export function useCombinaciones(): UseCombinacionesResult {
  const [combinaciones, setCombinaciones] = useState<CombinacionNtcInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await sysadminService.listarCombinaciones();
        if (res.success) setCombinaciones(res.combinaciones);
        else setError('Error al cargar combinaciones');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar combinaciones');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return { combinaciones, isLoading, error };
}

// ========================================
// Hook: NTCs Disponibles
// ========================================

interface UseNtcsDisponiblesResult {
  ntcs: NtcDisponible[];
  isLoading: boolean;
  error: string | null;
}

export function useNtcsDisponibles(): UseNtcsDisponiblesResult {
  const [ntcs, setNtcs] = useState<NtcDisponible[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await sysadminService.listarNtcsDisponibles();
        if (res.success) setNtcs(res.ntcs);
        else setError('Error al cargar NTCs');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al cargar NTCs');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return { ntcs, isLoading, error };
}

// ========================================
// Hook: Requisitos por Empresa
// ========================================

interface UseRequisitosEmpresaResult {
  requisitos: RequisitoEmpresaEstado[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  asociarRequisito: (requisitoId: string) => Promise<void>;
  desasociarRequisito: (requisitoId: string) => Promise<void>;
  asociarBatch: (requisitoIds: string[]) => Promise<void>;
  desasociarBatch: (requisitoIds: string[]) => Promise<void>;
}

export function useRequisitosEmpresa(nit: string, ntcId?: number): UseRequisitosEmpresaResult {
  const [requisitos, setRequisitos] = useState<RequisitoEmpresaEstado[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequisitos = useCallback(async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarRequisitosEmpresa(nit, ntcId);
      if (res.success) setRequisitos(res.requisitos);
      else setError('Error al cargar requisitos');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar requisitos');
    } finally {
      setIsLoading(false);
    }
  }, [nit, ntcId]);

  useEffect(() => {
    fetchRequisitos();
  }, [fetchRequisitos]);

  const asociarRequisito = useCallback(
    async (requisitoId: string) => {
      await sysadminService.asociarRequisito(nit, requisitoId);
      await fetchRequisitos();
    },
    [nit, fetchRequisitos]
  );

  const desasociarRequisito = useCallback(
    async (requisitoId: string) => {
      await sysadminService.desasociarRequisito(nit, requisitoId);
      await fetchRequisitos();
    },
    [nit, fetchRequisitos]
  );

  const asociarBatch = useCallback(
    async (requisitoIds: string[]) => {
      await sysadminService.asociarRequisitosBatch(nit, requisitoIds);
      await fetchRequisitos();
    },
    [nit, fetchRequisitos]
  );

  const desasociarBatch = useCallback(
    async (requisitoIds: string[]) => {
      await sysadminService.desasociarRequisitosBatch(nit, requisitoIds);
      await fetchRequisitos();
    },
    [nit, fetchRequisitos]
  );

  return { requisitos, isLoading, error, refetch: fetchRequisitos, asociarRequisito, desasociarRequisito, asociarBatch, desasociarBatch };
}

// ========================================
// Hook: Asociaciones entre Requisitos
// ========================================

interface UseAsociacionesResult {
  data: ListarAsociacionesResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  crearAsociacionesBatch: (data: CrearAsociacionesBatchRequest) => Promise<void>;
  eliminarAsociacionesBatch: (data: EliminarAsociacionesBatchRequest) => Promise<void>;
}

export function useAsociaciones(combinacionId: number | undefined): UseAsociacionesResult {
  const [data, setData] = useState<ListarAsociacionesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAsociaciones = useCallback(async () => {
    if (!combinacionId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarAsociaciones(combinacionId);
      if (res.success) {
        setData(res);
      } else {
        setError('Error al cargar asociaciones');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asociaciones');
    } finally {
      setIsLoading(false);
    }
  }, [combinacionId]);

  useEffect(() => {
    fetchAsociaciones();
  }, [fetchAsociaciones]);

  const crearAsociacionesBatch = useCallback(
    async (batchData: CrearAsociacionesBatchRequest) => {
      await sysadminService.crearAsociacionesBatch(batchData);
      await fetchAsociaciones();
    },
    [fetchAsociaciones]
  );

  const eliminarAsociacionesBatch = useCallback(
    async (batchData: EliminarAsociacionesBatchRequest) => {
      await sysadminService.eliminarAsociacionesBatch(batchData);
      await fetchAsociaciones();
    },
    [fetchAsociaciones]
  );

  return { data, isLoading, error, refetch: fetchAsociaciones, crearAsociacionesBatch, eliminarAsociacionesBatch };
}

// ========================================
// Hook: Ternas
// ========================================

interface UseTernasResult {
  data: ListarTernasResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useTernas(combinacionId: number | undefined): UseTernasResult {
  const [data, setData] = useState<ListarTernasResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTernas = useCallback(async () => {
    if (!combinacionId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarTernas(combinacionId);
      if (res.success) {
        setData(res);
      } else {
        setError('Error al cargar ternas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar ternas');
    } finally {
      setIsLoading(false);
    }
  }, [combinacionId]);

  useEffect(() => {
    fetchTernas();
  }, [fetchTernas]);

  return { data, isLoading, error, refetch: fetchTernas };
}

// ========================================
// Hook: Empresas por Combinación
// ========================================

interface UseEmpresasPorCombinacionResult {
  empresas: EmpresaPorCombinacion[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useEmpresasPorCombinacion(combinacionId: number | undefined): UseEmpresasPorCombinacionResult {
  const [empresas, setEmpresas] = useState<EmpresaPorCombinacion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmpresas = useCallback(async () => {
    if (!combinacionId) {
      setEmpresas([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarEmpresasPorCombinacion(combinacionId);
      if (res.success) {
        setEmpresas(res.empresas);
      } else {
        setError('Error al cargar empresas');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  }, [combinacionId]);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  return { empresas, isLoading, error, refetch: fetchEmpresas };
}
// ========================================
// Hook: Crear Requisito
// ========================================

interface UseCrearRequisitoResult {
  isLoading: boolean;
  error: string | null;
  crearRequisito: (data: CrearRequisitoRequest) => Promise<CrearRequisitoResponse>;
}

export function useCrearRequisito(): UseCrearRequisitoResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crearRequisito = useCallback(async (data: CrearRequisitoRequest): Promise<CrearRequisitoResponse> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await sysadminService.crearRequisito(data);
      if (!result.success) {
        setError(result.message || 'Error al crear requisito');
      }
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al crear requisito';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, crearRequisito };
}

// ========================================
// Hook: Requisitos Disponibles (vista global)
// ========================================

interface UseRequisitosDisponiblesResult {
  requisitos: RequisitoSysadmin[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  actualizarRequisito: (requisitoId: string, data: ActualizarRequisitoRequest) => Promise<ActualizarRequisitoResponse>;
}

export function useRequisitosDisponibles(ntcId?: number): UseRequisitosDisponiblesResult {
  const [requisitos, setRequisitos] = useState<RequisitoSysadmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequisitos = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarRequisitosDisponibles(ntcId);
      if (res.success) setRequisitos(res.requisitos);
      else setError('Error al cargar requisitos');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar requisitos');
    } finally {
      setIsLoading(false);
    }
  }, [ntcId]);

  useEffect(() => {
    fetchRequisitos();
  }, [fetchRequisitos]);

  const actualizarRequisito = useCallback(
    async (requisitoId: string, data: ActualizarRequisitoRequest): Promise<ActualizarRequisitoResponse> => {
      const result = await sysadminService.actualizarRequisito(requisitoId, data);
      await fetchRequisitos();
      return result;
    },
    [fetchRequisitos]
  );

  return { requisitos, isLoading, error, refetch: fetchRequisitos, actualizarRequisito };
}

// ========================================
// Hook: Auditoría
// ========================================

interface UseAuditoriaResult {
  empresaDetail: DashboardEmpresa | null;
  loadingDetail: boolean;
  error: string | null;
  fetchDetail: (nit: string) => Promise<void>;
  actualizarAsesoria: (asesoriaId: number, data: Record<string, unknown>) => Promise<void>;
  actualizarEvaluacion: (evaluacionId: number, data: Record<string, unknown>) => Promise<void>;
  actualizarAudio: (audioId: number, data: Record<string, unknown>) => Promise<void>;
}

export function useAuditoria(): UseAuditoriaResult {
  const [empresaDetail, setEmpresaDetail] = useState<DashboardEmpresa | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async (nit: string) => {
    if (!nit) return;
    setLoadingDetail(true);
    setError(null);
    try {
      const res = await dashboardService.getEmpresa(nit);
      if (res.success) {
        setEmpresaDetail(res.data);
      } else {
        // DashboardEmpresaResponse might not have message, let's cast or handle
        const errorRes = res as unknown as GenericResponse;
        setError(errorRes.message || 'Error al cargar detalles de la empresa');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar detalles');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const actualizarAsesoria = useCallback(async (asesoriaId: number, data: Record<string, unknown>) => {
    const res = await sysadminService.actualizarAsesoriaAuditoria(asesoriaId, data);
    if (!res.success) throw new Error(res.message || 'Error al actualizar asesoría');
  }, []);

  const actualizarEvaluacion = useCallback(async (evaluacionId: number, data: Record<string, unknown>) => {
    const res = await sysadminService.actualizarEvaluacionAuditoria(evaluacionId, data);
    if (!res.success) throw new Error(res.message || 'Error al actualizar evaluación');
  }, []);

  const actualizarAudio = useCallback(async (audioId: number, data: Record<string, unknown>) => {
    const res = await sysadminService.actualizarAudioAuditoria(audioId, data);
    if (!res.success) throw new Error(res.message || 'Error al actualizar audio');
  }, []);

  return {
    empresaDetail,
    loadingDetail,
    error,
    fetchDetail,
    actualizarAsesoria,
    actualizarEvaluacion,
    actualizarAudio,
  };
}

// ========================================
// Hook: Audios (Sysadmin)
// ========================================

interface UseAudiosSysadminResult {
  audios: AudioSysadminDTO[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  eliminarAudio: (audioId: number) => Promise<void>;
  actualizarAudio: (audioId: number, data: Record<string, unknown>) => Promise<void>;
  compartirAudio: (audioId: number, correos: string[]) => Promise<CompartirAudioResponse>;
}

export function useAudiosSysadmin(): UseAudiosSysadminResult {
  const [audios, setAudios] = useState<AudioSysadminDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAudios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarAudios();
      if (res.success) {
        setAudios(res.audios);
      } else {
        setError('Error al cargar audios');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar audios');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudios();
  }, [fetchAudios]);

  const eliminarAudio = useCallback(
    async (audioId: number) => {
      try {
        const res = await sysadminService.eliminarAudio(audioId);
        if (res.success) {
          await fetchAudios();
        } else {
          throw new Error('Error al eliminar audio');
        }
      } catch (err) {
        throw err instanceof Error ? err : new Error('Error al eliminar audio');
      }
    },
    [fetchAudios]
  );

  const actualizarAudio = useCallback(
    async (audioId: number, data: Record<string, unknown>) => {
      try {
        const res = await sysadminService.actualizarAudioAuditoria(audioId, data);
        if (res.success) {
          await fetchAudios();
        } else {
          throw new Error('Error al actualizar audio');
        }
      } catch (err) {
        throw err instanceof Error ? err : new Error('Error al actualizar audio');
      }
    },
    [fetchAudios]
  );

  const compartirAudio = useCallback(async (audioId: number, correos: string[]) => {
    try {
      const res = await sysadminService.compartirAudio(audioId, { correos });
      return res;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al compartir audio');
    }
  }, []);

  return { audios, isLoading, error, refetch: fetchAudios, eliminarAudio, actualizarAudio, compartirAudio };
}

// ========================================
// Hook: Audios ELIMINADO por reasignación de asesor (revisión antes de borrado físico)
// ========================================

export function useAudiosEliminadosPorReasignacion() {
  const [audios, setAudios] = useState<AudioEliminadoReasignacionDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAudios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listarAudiosEliminadosPorReasignacion();
      if (res.success) {
        setAudios(res.audios);
      } else {
        setError('Error al cargar audios eliminados por reasignación');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar audios eliminados por reasignación');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAudios();
  }, [fetchAudios]);

  const eliminarSeleccionados = useCallback(
    async (audioIds: number[]) => {
      setIsDeleting(true);
      try {
        const resultados = await Promise.allSettled(audioIds.map((id) => sysadminService.eliminarAudio(id)));
        const fallidos = resultados.filter((r) => r.status === 'rejected').length;
        await fetchAudios();
        return { total: audioIds.length, fallidos };
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchAudios]
  );

  return { audios, isLoading, error, isDeleting, refetch: fetchAudios, eliminarSeleccionados };
}

// ========================================
// Hook: Recalcular Cumplimiento NTC
// ========================================

interface UseRecalcularCumplimientoResult {
  isLoading: boolean;
  error: string | null;
  recalcular: () => Promise<number>;
}

export function useRecalcularCumplimiento(): UseRecalcularCumplimientoResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recalcular = useCallback(async (): Promise<number> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.recalcularCumplimientoTodasNTCS();
      if (!res || !res.success) {
        const msg = (res as unknown as { message?: string })?.message || 'Error al recalcular cumplimiento';
        setError(msg);
        throw new Error(msg);
      }
      return res.updated ?? 0;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, recalcular };
}

export function useSincronizarCumplimiento() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sincronizar = useCallback(async (): Promise<number> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.sincronizarCumplimiento();
      if (!res || !res.success) {
        const msg = (res as unknown as { message?: string })?.message || 'Error al sincronizar cumplimiento';
        setError(msg);
        throw new Error(msg);
      }
      return res.processed ?? 0;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, sincronizar };
}

export function useSincronizarEmpresa() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sincronizar = useCallback(async (nit: string): Promise<string> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.sincronizarEmpresa(nit);
      if (!res || !res.success) {
        const msg = (res as unknown as { message?: string })?.message || 'Error al sincronizar empresa';
        setError(msg);
        throw new Error(msg);
      }
      return res.nit;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, sincronizar };
}

// ========================================
// Hook: Google Drive (Sysadmin)
// ========================================

interface UseDriveSysadminResult {
  isLoading: boolean;
  error: string | null;
  copyFileAndShare: (data: CopyDriveFileRequest) => Promise<CopyDriveFileResponse>;
}

export function useDriveSysadmin(): UseDriveSysadminResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copyFileAndShare = useCallback(async (data: CopyDriveFileRequest) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.copyDriveFileAndShare(data);
      if (!res.success) {
        throw new Error(res.message || 'Error copiando el archivo en Drive');
      }
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error copiando el archivo en Drive';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, copyFileAndShare };
}
export function useExportComplianceData() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.exportComplianceData();
      if (!res || !res.success) {
        throw new Error('Error al exportar datos de cumplimiento');
      }
      return res.data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al exportar datos de cumplimiento';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, exportData };
}

export function useImportComplianceData() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const importData = useCallback(async (data: unknown[]) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.importComplianceData(data);
      if (!res || !res.success) {
        throw new Error('Error al importar datos de cumplimiento');
      }
      return res.updated ?? 0;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al importar datos de cumplimiento';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, importData };
}

export function useBackfillOrdenAsesorias() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const backfillOrden = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.backfillOrdenAsesorias();
      if (!res || !res.success) {
        throw new Error('Error al hacer backfill de orden');
      }
      return res.data?.actualizadas ?? 0;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al hacer backfill de orden';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, backfillOrden };
}

export function useMigrarCompromisosConclusiiones() {
  const [isLoading, setIsLoading] = useState(false);

  const migrar = useCallback(async (): Promise<{ migradas: number; omitidas: number }> => {
    setIsLoading(true);
    try {
      const res = await sysadminService.migrarCompromisosConclusiiones();
      if (!res.success) throw new Error('Error al migrar');
      return { migradas: res.migradas, omitidas: res.omitidas };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, migrar };
}

export function useEmpresasAnomaliasNtc() {
  const [empresas, setEmpresas] = useState<EmpresaAnomaliaDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await sysadminService.listarEmpresasConAnomaliasNtc();
      setEmpresas(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { empresas, isLoading, error, refetch: fetchData };
}

// ========================================
// Hook: Empresas Desincronizadas
// ========================================

export function useEmpresasDesincronizadas() {
  const [empresas, setEmpresas] = useState<EmpresaDesincronizadaDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscar = useCallback(async (): Promise<EmpresaDesincronizadaDto[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.obtenerEmpresasDesincronizadas();
      if (!res || !res.success) {
        throw new Error((res as unknown as { message?: string })?.message || 'Error al buscar empresas desincronizadas');
      }
      setEmpresas(res.data);
      return res.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al buscar empresas desincronizadas');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeEmpresa = useCallback((nitId: string) => {
    setEmpresas((prev) => prev.filter((e) => e.nitId !== nitId));
  }, []);

  return { empresas, isLoading, error, buscar, removeEmpresa };
}

export function useEmpresasHorasDesincronizadas() {
  const [empresas, setEmpresas] = useState<EmpresaHorasDesincronizadaDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscar = useCallback(async (): Promise<EmpresaHorasDesincronizadaDto[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.obtenerEmpresasConHorasDesincronizadas();
      if (!res || !res.success) {
        throw new Error((res as unknown as { message?: string })?.message || 'Error al buscar empresas con horas desincronizadas');
      }
      setEmpresas(res.data);
      return res.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al buscar empresas con horas desincronizadas');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const removeEmpresa = useCallback((nitId: string) => {
    setEmpresas((prev) => prev.filter((e) => e.nitId !== nitId));
  }, []);

  return { empresas, isLoading, error, buscar, removeEmpresa };
}

export function useRecalcularHorasEmpresa() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recalcular = useCallback(async (nit: string): Promise<string> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.recalcularHorasEmpresa(nit);
      if (!res || !res.success) {
        const msg = (res as unknown as { message?: string })?.message || 'Error al recalcular horas';
        setError(msg);
        throw new Error(msg);
      }
      return res.nit;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, recalcular };
}

/**
 * Asesorías FINALIZADA con audios RECHAZADO cuyas horas quedaron infladas (nunca se descuentan si
 * el audio no fue reasignado). Ver detectar-asesorias-horas-por-rechazo.ts en el backend.
 */
export function useAsesoriasHorasPorRechazo() {
  const [asesorias, setAsesorias] = useState<AsesoriaHorasPorRechazoDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCorrigiendo, setIsCorrigiendo] = useState(false);

  const fetchAsesorias = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.obtenerAsesoriasFinalizadasConAudiosRechazados();
      if (res.success) {
        setAsesorias(res.data);
      } else {
        setError('Error al cargar asesorías con horas por rechazo');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar asesorías con horas por rechazo');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAsesorias();
  }, [fetchAsesorias]);

  const corregir = useCallback(async (asesoriaId: number) => {
    setIsCorrigiendo(true);
    try {
      const res = await sysadminService.corregirHorasAsesoriaPorRechazo(asesoriaId);
      if (!res.success) throw new Error('Error al corregir horas de la asesoría');
      setAsesorias((prev) => prev.filter((a) => a.asesoriaId !== asesoriaId));
      return res;
    } finally {
      setIsCorrigiendo(false);
    }
  }, []);

  return { asesorias, isLoading, error, isCorrigiendo, corregir };
}

export function useAsesoriasSinEvaluacionAprobada() {
  const [asesorias, setAsesorias] = useState<AsesoriaSinEvaluacionAprobadaDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buscar = useCallback(async (): Promise<AsesoriaSinEvaluacionAprobadaDto[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.obtenerAsesoriasSinEvaluacionAprobada();
      if (!res || !res.success) {
        throw new Error((res as unknown as { message?: string })?.message || 'Error al listar asesorias sin evaluacion aprobada');
      }
      setAsesorias(res.data);
      return res.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al listar asesorias sin evaluacion aprobada');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { asesorias, isLoading, error, buscar };
}

// ========================================
// Hook: Gestión y Normalización de Audios en Drive
// ========================================

export function useDriveAudioManagement() {
  const [files, setFiles] = useState<DriveFileItemDto[]>([]);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroupDto[]>([]);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargarArchivos = useCallback(async (folderId: string, nit?: string) => {
    if (!folderId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.listDriveFolderFiles(folderId, nit);
      if (res.success) {
        setFiles(res.files || []);
      } else {
        setError('Error al listar archivos de la carpeta');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al listar archivos de la carpeta');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const renombrarArchivo = useCallback(async (fileId: string, newName: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const res = await sysadminService.renameDriveFile(fileId, newName);
      if (res.success) {
        setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, name: res.file.name, suggestedName: res.file.name } : f)));
      }
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al renombrar archivo';
      setError(msg);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const autoNormalizar = useCallback(
    async (folderId: string, apply = false) => {
      setIsProcessing(true);
      setError(null);
      try {
        const res = await sysadminService.autoNormalizeFolderAudios(folderId, apply);
        if (apply && res.success) {
          await cargarArchivos(folderId);
        }
        return res;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al normalizar audios';
        setError(msg);
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [cargarArchivos]
  );

  const detectarDuplicados = useCallback(async (folderId: string, nit?: string) => {
    setIsProcessing(true);
    setError(null);
    try {
      const res = await sysadminService.detectFolderDuplicates(folderId, nit);
      if (res.success) {
        setDuplicateGroups(res.groups || []);
        setTotalDuplicates(res.totalDuplicates || 0);
      }
      return res;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al detectar duplicados';
      setError(msg);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const eliminarArchivo = useCallback(
    async (fileId: string, folderId?: string, nit?: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        const res = await sysadminService.deleteDriveFile(fileId);
        if (res.success) {
          setFiles((prev) => prev.filter((f) => f.id !== fileId));
          if (folderId) {
            await detectarDuplicados(folderId, nit);
          }
        }
        return res;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error al eliminar archivo';
        setError(msg);
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [detectarDuplicados]
  );

  const eliminarLote = useCallback(
    async (fileIds: string[], folderId?: string, nit?: string) => {
      setIsProcessing(true);
      setError(null);
      try {
        const res = await sysadminService.batchDeleteDriveFiles(fileIds);
        if (res.success && Array.isArray(res.deleted)) {
          const deletedIds = new Set(
            res.deleted
              .filter((d: { success: boolean; fileId: string }) => d.success)
              .map((d: { success: boolean; fileId: string }) => d.fileId)
          );
          setFiles((prev) => prev.filter((f) => !deletedIds.has(f.id)));
          if (folderId) {
            await detectarDuplicados(folderId, nit);
          }
        }
        return res;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error en eliminación masiva';
        setError(msg);
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [detectarDuplicados]
  );

  return {
    files,
    duplicateGroups,
    totalDuplicates,
    isLoading,
    isProcessing,
    error,
    cargarArchivos,
    renombrarArchivo,
    autoNormalizar,
    detectarDuplicados,
    eliminarArchivo,
    eliminarLote,
  };
}
